import type {JSXMapSerializer} from '@prismicio/react'
import Image from 'next/image'
import {DOWNLOAD_SKILL_ANCHOR} from '../constants'
import {createSlugFromText} from '../utils'

/**
 * PrismicRichText components configuration for blog post content
 * Defines how each rich text element should be rendered
 */
export const richTextComponents: JSXMapSerializer = {
  heading3: ({children, node}) => {
    const id = node.text ? createSlugFromText(node.text) : ''

    return (
      <h3 id={id} className="scroll-mt-8 pt-4 text-24 font-medium leading-8 text-moss-black lg:pt-8">
        {children}
      </h3>
    )
  },

  heading4: ({children}) => <h4 className="pt-4 text-18 font-medium leading-6 text-moss-black lg:pt-8">{children}</h4>,

  heading5: ({children}) => <h5 className="text-button-s pb-4 pt-12 text-moss-black">{children}</h5>,

  strong: ({children}) => <span className="font-medium text-moss-grey-400">{children}</span>,
  hyperlink: ({children, node}) => {
    const isSkillAnchor = node.data.url === `#${DOWNLOAD_SKILL_ANCHOR}`

    if (isSkillAnchor) {
      return (
        <a
          href={node.data.url}
          className="inline-flex items-center gap-1.5 rounded border border-green-neon/35 bg-green-neon/[20%] px-2 py-px align-middle text-16 text-dark-green"
        >
          <span className="inline-block size-1.5 animate-epg-dot-pulse rounded-full bg-green-neon" />
          {children}
        </a>
      )
    }

    return (
      <a href={node.data.url} className="text-tg-body-l text-ui-blue-450">
        {children}
      </a>
    )
  },

  list: ({children}) => <ul className="text-tg-body-l list-none space-y-2 text-moss-grey-550">{children}</ul>,

  oList: ({children}) => (
    <ol className="text-tg-body-l list-decimal space-y-2 py-4 pl-5 text-moss-grey-550">{children}</ol>
  ),

  oListItem: ({children}) => <li className="list-item pl-2 text-moss-grey-550">{children}</li>,

  listItem: ({children}) => (
    <li className="relative pl-7 text-moss-grey-550 before:absolute before:left-2.5 before:top-3 before:block before:h-[3px] before:w-[3px] before:bg-moss-grey-550">
      {children}
    </li>
  ),

  image: ({node}) => (
    <Image
      src={node.url || ''}
      alt={node.alt || 'Blog image'}
      width={600}
      height={345}
      className="w-full rounded-lg pb-12"
    />
  ),
}
